import React from 'react';
import { Typography } from '@mui/material';
import { EventOutlined } from '@mui/icons-material';
import { LightChip, Button, useBreakpoint } from '@uplift/ui-v2';
import { BaseActivityItem, BaseActivityItemProps } from '../BaseActivityItem';
import { useCurrentuser } from '@uplift/contexts';
import { Appointment, PatientPsychiatryAppointmentActivity } from '@uplift/api';
import { Link } from 'react-router-dom';
import { providerSubmitSessionRoute } from '@uplift/constants';


export type PsychiatryAppointmentActivityItemProps = {
  isFirstSession: boolean;
  appointment: Appointment;
  onClick?: () => void;
  plan?: PatientPsychiatryAppointmentActivity['plan'];
  provider: {
    id: string;
    firstName: string;
    lastName: string;
  };
};


export const PsychiatrySessionBaseActivityItem: React.FC<PsychiatryAppointmentActivityItemProps & Pick<BaseActivityItemProps, 'onClick' | 'rightContent' | 'chip' | 'highlighted'>> = (props) => {
  const title = React.useMemo(() => {
    if (props.isFirstSession) {
      return 'Psych consultation';
    }

    return 'Medication Management';
  }, [ props.isFirstSession ]);

  return (
    <BaseActivityItem
      onClick={props.onClick}
      title={title}
      color="secondary"
      icon={EventOutlined}
      chip={props.chip}
      highlighted={props.highlighted}
      rightContent={props.rightContent ?? <Typography variant="body2" color="textPrimary">with {props.provider.firstName[0]}. {props.provider.lastName}</Typography>}
      children={props.children}
    />
  );
};


export const PsychiatrySessionCompletedOnCanvasActivityItem: React.FC<PsychiatryAppointmentActivityItemProps> = (props) => {
  const me = useCurrentuser();
  const chip = React.useMemo(() => {
    if (me.id === props.provider.id) {
      return <LightChip label="Notes on Canvas" size="small" />;
    }

    return <LightChip label="Notes unavailable" size="small" />;
  }, [ me.id, props.provider.id ]);

  return (
    <PsychiatrySessionBaseActivityItem
      chip={chip}
      {...props}
    />
  );
};


export const PsychiatrySessionCompletedActivityItem: React.FC<PsychiatryAppointmentActivityItemProps> = ({ appointment, ...props }) => {
  const me = useCurrentuser();
  const isCurrentUserSession = me.id === props.provider.id;
  const breakpoint = useBreakpoint();

  const chip = React.useMemo(() => {
    if (!isCurrentUserSession) {
      return <LightChip label="Notes unavailable" size="small" />;
    }

    return null;
  }, [ isCurrentUserSession ]);

  const rightContent = React.useMemo(() => {
    if (isCurrentUserSession) {
      return (
        <Button
          component={Link}
          to={providerSubmitSessionRoute.createLink({ appointmentId: appointment.id })}
          variant="contained"
          color="secondary"
          size="small"
        >
          {breakpoint.md ? 'Complete Note' : 'Submit'}
        </Button>
      );
    }

    return null;
  }, [ isCurrentUserSession, appointment.id, breakpoint.md ]);

  return (
    <PsychiatrySessionBaseActivityItem
      highlighted
      appointment={appointment}
      provider={props.provider}
      chip={chip}
      isFirstSession={props.isFirstSession}
      rightContent={rightContent}
    />
  );
};


export const PsychiatrySessionSubmittedActivityItem: React.FC<PsychiatryAppointmentActivityItemProps> = (props) => {
  const plan = React.useMemo(() => {
    return [
      { title: 'Start', value: props.plan?.treatmentPlanStart },
      { title: 'Stop', value: props.plan?.treatmentPlanStop },
      { title: 'Change', value: props.plan?.treatmentPlanChange },
      { title: 'Continue', value: props.plan?.treatmentPlanContinue },
    ].filter(v => v.value);
  }, [ props.plan ]);

  return (
    <PsychiatrySessionBaseActivityItem {...props}>
      {plan?.length ? plan?.map(p => (
        <Typography color="textSecondary" component="span" key={p.title}>
          <strong style={{ fontWeight: 500 }}>{p.title}</strong>: {p.value}.{' '}
        </Typography>
      )) : null}
    </PsychiatrySessionBaseActivityItem>
  );
};
